import { iceHints, IceBoard, NO_HINTS, type IceCameraView, type Square } from '@halvard/board3d';
import { ice, squareName } from '@halvard/rules';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useStore } from 'zustand';
import {
  setMotionPreference,
  useMotionSettings,
  type MotionPreference,
} from '../../hooks/useReducedMotion';
import { SideDot } from '../../components/SideDot';
import { t, useT } from '../../i18n';
import { boardText } from '../../i18n/board';
import '../LocalGame.css';
import './IceLocalGame.css';
import { describeIceEvents, outcomeText, shoveSums, warriorName } from './describe';
import { isBotTurn, sideToMoveText, type IceGameStoreApi, type IcePlayers } from './store';

const VIEWS: readonly IceCameraView[] = ['brand', 'rime', 'top'];
const MOTION: readonly MotionPreference[] = ['auto', 'on', 'off'];

/** The last action's squares: where the warrior started and where it acted. */
function lastSquares(game: ice.IceState, before: ice.IceState | undefined): Square[] {
  if (!before) return [];
  const moved = game.warriors.filter((w) => {
    const old = before.warriors.find((b) => b.id === w.id);
    return !old || old.r !== w.r || old.c !== w.c;
  });
  const squares = moved.flatMap((w) => {
    const old = before.warriors.find((b) => b.id === w.id);
    return old ? [old, w] : [w];
  });
  // A shover lands where its target stood: keep each square once.
  return squares
    .filter((sq, i) => squares.findIndex((o) => o.r === sq.r && o.c === sq.c) === i)
    .map(({ r, c }) => ({ r, c }));
}

export interface IceLocalGameProps {
  store: IceGameStoreApi;
  /** Local games: start over. Online games leave them out and use `resultActions`. */
  onNewLake?: () => void;
  onReplay?: () => void;
  /** Buttons under a finished game's result, in place of Replay / New lake. */
  resultActions?: ReactNode;
  /** Drawn over the board, e.g. the screen between the two games of a match. */
  overlay?: ReactNode;
  /** Replaces the turn line, e.g. "Waiting for your friend". */
  status?: string;
  /** Extra controls at the top of the panel (bot games). */
  panelExtra?: ReactNode;
}

/** "Hard bot", "You", the opponent's name, or "Rime player" when two people share the screen. */
export function playerLabel(
  players: IcePlayers,
  side: ice.Side,
  names: Partial<Record<ice.Side, string>> = {},
): string {
  const { game, bots, sides } = t();
  const player = players[side];
  if (player === 'remote') return names[side] ?? game.opponent;
  if (player !== 'human') return game.bot(bots[player].name);
  const other = players[side === 'brand' ? 'rime' : 'brand'];
  return other === 'human' ? game.sidePlayer(sides[side]) : game.you;
}

export function IceLocalGame(props: IceLocalGameProps) {
  const { store, onNewLake, onReplay, panelExtra, resultActions, overlay } = props;
  const s = useStore(store);
  const tr = useT();
  const text = useMemo(() => boardText(tr), [tr]);
  const g = tr.game;
  const game = s.game;
  const motion = useMotionSettings();
  const reducedMotion = motion.reduced;
  const [sheetOpen, setSheetOpen] = useState(false);
  const log = useRef<HTMLOListElement>(null);
  const over = game.phase === 'over';

  const botTurn = isBotTurn(s);
  // No hints while a bot or an online opponent is to move (or nobody can move yet), nor once
  // the game is over.
  const hints = useMemo(
    () => (botTurn ? NO_HINTS : iceHints(game, s.selectedId, s.target, s.focus)),
    [botTurn, game, s.selectedId, s.target, s.focus],
  );
  const selected = game.warriors.find((w) => w.id === s.selectedId) ?? null;
  const lastMove = useMemo(() => lastSquares(game, s.history.at(-1)?.before), [game, s.history]);
  const mustPass = useMemo(() => {
    const legal = ice.legalIceActions(game);
    return legal.length === 1 && legal[0]?.kind === 'pass';
  }, [game]);
  const holders = ice.runestoneHolders(game);
  const { roundLimit, gloryTarget } = game.config;
  const brandTurnsLeft = roundLimit - game.round + (game.turn === 'brand' ? 1 : 0);
  const result =
    over && game.winner && game.winReason ? outcomeText(game.winner, game.winReason) : null;
  const anyBot = s.players.brand !== 'human' || s.players.rime !== 'human';
  const turnLine = botTurn
    ? g.botTurn(
        tr.sides[game.turn],
        playerLabel(s.players, game.turn, s.names),
        s.thinking ? 'thinking' : game.phase === 'placing' ? 'placing' : 'moving',
      )
    : sideToMoveText(game);
  const status = result ? result.title : (props.status ?? turnLine);
  const left = ice.toPlace(game, game.turn);

  useEffect(() => {
    const list = log.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [s.history]);

  const choice = hints.choice;
  const choiceShover = choice && game.warriors.find((w) => w.id === choice.action.id);
  const choiceTarget =
    choice && ice.warriorAt(game, choice.action.target.r, choice.action.target.c);

  return (
    <div className="game-layout">
      <section className="game-boardArea" aria-label={g.lakeArea}>
        <IceBoard
          state={game}
          hints={hints}
          selected={selected ? { r: selected.r, c: selected.c } : null}
          cursor={s.cursor}
          lastMove={lastMove}
          playback={s.playback}
          view={s.view}
          facing={s.view === 'rime' ? 'rime' : 'brand'}
          reducedMotion={reducedMotion}
          label={g.boardLabel(game.lake.seed, status)}
          text={text}
          onSquareActivate={s.activate}
          onSquareFocus={s.setFocus}
          onCursorChange={s.setCursor}
          onCancel={s.cancel}
        />
        {result && (
          <div className="game-banner" role="alert">
            <p className="game-bannerTitle">{result.title}</p>
            <p className="game-bannerDetail">
              {result.detail} {g.gloryResult(game.glory, gloryTarget)}
            </p>
            <div className="game-row">
              {resultActions ?? (
                <>
                  <button type="button" className="btn btn-primary" onClick={onReplay}>
                    {g.replay}
                  </button>
                  <button type="button" className="btn" onClick={onNewLake}>
                    {g.newLake}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        {overlay}
      </section>

      <aside className={`game-panel ${sheetOpen ? 'game-open' : ''}`} aria-label={g.controls}>
        <button
          type="button"
          className="game-sheetHandle"
          aria-expanded={sheetOpen}
          aria-controls="ice-panel-body"
          onClick={() => setSheetOpen((open) => !open)}
        >
          <span className="game-turn">
            {!result && <SideDot side={game.turn} />}
            {status}
          </span>
          <span className="label">{sheetOpen ? g.hide : g.showControls}</span>
        </button>

        <div id="ice-panel-body" className="game-panelBody">
          <div className="game-section">
            <p className="game-turnDesktop">
              {!result && <SideDot side={game.turn} />}
              <span>{status}</span>
              {game.phase !== 'placing' && (
                <span className="label">{g.round(game.round, roundLimit)}</span>
              )}
            </p>
            <p className="label">{g.lake(game.lake.seed)}</p>
            {anyBot && (
              <ul className="ice-stones" aria-label={g.players}>
                {(['brand', 'rime'] as const).map((side) => (
                  <li key={side}>
                    <SideDot side={side} />
                    <span>{tr.sides[side]}</span>
                    <span className="game-hint">{playerLabel(s.players, side, s.names)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {panelExtra}

          <div className="game-section">
            <h2 className="label">{g.glory}</h2>
            <div
              className="ice-meter"
              role="meter"
              aria-label={g.gloryMeter}
              aria-valuemin={0}
              aria-valuemax={gloryTarget}
              aria-valuenow={game.glory}
            >
              <span style={{ width: `${Math.min(100, (game.glory / gloryTarget) * 100)}%` }} />
            </div>
            <p className="game-hint">
              {g.gloryLine(game.glory, gloryTarget)}
              {!over && game.phase === 'playing'
                ? g.gloryNeeds(Math.max(0, gloryTarget - game.glory), brandTurnsLeft)
                : ''}
              .
            </p>
            <ul className="ice-stones" aria-label={g.runestonesLabel}>
              {tr.runestones.map((stoneName, i) => {
                const holder = holders[i] ?? null;
                return (
                  <li key={stoneName}>
                    {holder ? (
                      <SideDot side={holder} />
                    ) : (
                      <span className="side-dot ice-dotEmpty" aria-hidden="true" />
                    )}
                    <span>{stoneName}</span>
                    <span className="game-hint">
                      {holder ? g.holds(tr.sides[holder]) : g.nobody}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          {game.phase === 'placing' && !botTurn && (
            <div className="game-section">
              <h2 className="label">{g.placeHeading}</h2>
              <div className="segmented" role="group" aria-label={g.placeGroup}>
                {ice.WARRIOR_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={s.placeType === type}
                    disabled={left[type] === 0}
                    onClick={() => s.setPlaceType(type)}
                  >
                    {tr.warriors[type]} ×{left[type]}
                  </button>
                ))}
              </div>
              <p className="game-rule">{tr.warriorRule[s.placeType]}</p>
              <p className="game-hint">
                {g.placeHint(tr.sides[game.turn], game.turn === 'brand' ? '1–3' : '7–9')}
              </p>
            </div>
          )}

          {game.phase === 'playing' && !botTurn && (
            <div className="game-section">
              {selected ? (
                <>
                  <p className="game-pieceTitle">
                    <SideDot side={selected.side} />
                    <span>
                      {warriorName(selected)}{' '}
                      <span className="mono">{squareName(selected.r, selected.c)}</span>
                    </span>
                  </p>
                  <p className="game-rule">{tr.warriorRule[selected.type]}</p>
                  {choice && choiceShover && choiceTarget ? (
                    <div className="ice-shove" aria-live="polite">
                      <p>
                        <strong>{g.shoveTitle(squareName(choiceTarget.r, choiceTarget.c))}</strong>{' '}
                        {g.shoveFrom(squareName(choice.action.from.r, choice.action.from.c))}:{' '}
                        {shoveSums(choiceShover, choiceTarget, choice.strength, choice.wall)}
                      </p>
                      {hints.ghosts
                        .filter((ghost) => ghost.sunk)
                        .map((ghost) => (
                          <p
                            key={ghost.warrior.id}
                            className={ghost.warrior.side === selected.side ? 'ice-warn' : ''}
                          >
                            {tr.log.sunk(warriorName(ghost.warrior), ghost.sunk ?? 'off')}
                          </p>
                        ))}
                      <p className="game-hint">{s.target ? g.pointAnother : g.clickEnemy}</p>
                    </div>
                  ) : (
                    <p className="game-hint">{s.target ? g.shoveFails : g.dotsHelp}</p>
                  )}
                </>
              ) : (
                <p className="game-hint">{g.selectWarrior}</p>
              )}
            </div>
          )}

          <div className="game-section game-row">
            {mustPass && !over && !botTurn && (
              <button type="button" className="btn btn-small btn-primary" onClick={s.pass}>
                {g.pass}
              </button>
            )}
            <button
              type="button"
              className="btn btn-small"
              onClick={s.undo}
              disabled={
                s.history.length === 0 ||
                (s.players.brand !== 'human' && s.players.rime !== 'human') ||
                s.players.brand === 'remote' ||
                s.players.rime === 'remote'
              }
            >
              {g.undo}
            </button>
          </div>

          <div className="game-section">
            <h2 className="label">{g.cracks}</h2>
            {game.cracks.length === 0 ? (
              <p className="game-hint">
                {game.phase === 'playing'
                  ? g.groansAt(
                      Math.ceil(game.round / game.config.crackEvery) * game.config.crackEvery,
                    )
                  : g.noneYet}
              </p>
            ) : (
              <ul className="ice-cracks">
                {game.cracks.map((crack) => {
                  const opens = game.heights[crack.r]?.[crack.c] === 0;
                  const [before, word, after] = g.crack(crack.thawRound, opens);
                  return (
                    <li key={`${crack.r}:${crack.c}`}>
                      <span className="mono">{squareName(crack.r, crack.c)}</span> {before}
                      {opens ? <span className="ice-warn">{word}</span> : word}
                      {after}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="game-section">
            <h2 className="label">{g.log}</h2>
            {s.history.length === 0 ? (
              <p className="game-hint">{g.logEmpty}</p>
            ) : (
              <ol className="ice-log" ref={log} aria-label={g.moveLog}>
                {s.history.map((turn, i) => (
                  <li key={i}>
                    <SideDot side={turn.side} />
                    <span className="mono">{turn.notation}</span>
                    <span className="game-hint">
                      {describeIceEvents(turn.before, turn.events).join(' ')}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="game-section">
            <h2 className="label">{g.view}</h2>
            <div className="segmented" role="group" aria-label={g.cameraView}>
              {VIEWS.map((view) => (
                <button
                  key={view}
                  type="button"
                  aria-pressed={s.view === view}
                  onClick={() => s.setView(view)}
                >
                  {g.views[view]}
                </button>
              ))}
            </div>
            <p className="label ice-subhead">{g.animations}</p>
            <div className="segmented" role="group" aria-label={g.animations}>
              {MOTION.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={motion.preference === option}
                  onClick={() => setMotionPreference(option)}
                >
                  {g.motion[option]}
                </button>
              ))}
            </div>
            {motion.preference === 'auto' && motion.systemReduces && (
              <p className="game-hint">{g.reducedNote}</p>
            )}
          </div>

          {onNewLake && onReplay && (
            <div className="game-section game-row">
              <button type="button" className="btn btn-small" onClick={onNewLake}>
                {g.newLake}
              </button>
              <button type="button" className="btn btn-small" onClick={onReplay}>
                {g.replay}
              </button>
            </div>
          )}
        </div>
      </aside>

      <p className="visually-hidden" role="status" aria-live="polite">
        {s.announcement}
      </p>
    </div>
  );
}
